import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { API_GET_ENQUIRY_DETAILS, API_GET_INVOICE } from "../../Util/URLConstants";
import VMEDHeader from "../EnquiryDetails/VMEDHeader";
import VMEDBasicInfo from "../EnquiryDetails/VMEDBasicInfo";
import VMEDOptions from "../EnquiryDetails/VMEDOptions";
import VMEDMENUOPTIONS from "../EnquiryDetails/MenuOptions/VMEDMENUOPTIONS";
import VMEDEventTabs from "../EnquiryDetails/VMEDEventTabs";

export default function SuperAdminEnquiryDetails({ enquiryId }) {
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [events, setEvents] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [hasInvoice, setHasInvoice] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!enquiryId) return;
    setLoading(true);
    setCustomer(null);
    setEvents([]);
    setUserInfo(null);
    setHasInvoice(false);
    try {
      const [enquiryResponse, invoiceResponse] = await Promise.all([
        axios.get(API_GET_ENQUIRY_DETAILS, { params: { enquiry_id: enquiryId } }),
        axios.get(API_GET_INVOICE, { params: { enquiry_id: enquiryId } }).catch(() => null),
      ]);
      const body = enquiryResponse.data;
      const invoiceDetails = invoiceResponse?.data?.invoice_info || [];
      setHasInvoice(Array.isArray(invoiceDetails) && invoiceDetails.length > 0);
      if (body?.status_code === 200 && body.enquiry_info) {
        setCustomer(body.enquiry_info);
        setEvents(Array.isArray(body.event_info) ? body.event_info : []);
        setUserInfo(body.user_info || null);
      } else {
        toast.error(body?.message || "Could not load enquiry details.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load enquiry details.");
    } finally {
      setLoading(false);
    }
  }, [enquiryId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!customer) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        Enquiry not found.
      </Typography>
    );
  }

  return (
    <Box>
      <VMEDHeader customer={customer} events={events} refreshEnquiryDetails={fetchDetails} readOnly />
      <Divider sx={{ my: 1 }} />
      <VMEDBasicInfo customer={customer} />
      <VMEDOptions customer={customer} events={events} user_info={userInfo} hasInvoice={hasInvoice} readOnly />
      <VMEDMENUOPTIONS
        enquiry_id={enquiryId}
        customer={customer}
        events={events}
        user_info={userInfo}
        refreshEnquiryDetails={fetchDetails}
        readOnly
      />
      {events.length > 0 ? (
        <VMEDEventTabs events={events} customer={customer} hasInvoice={hasInvoice} readOnly />
      ) : (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No events linked to this enquiry.
        </Typography>
      )}
    </Box>
  );
}
